import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import useService from '../../../hooks/useService';
import Page from '../../../components/Page/Page';
import ServiceOverview from './ServiceOverview';
import useServiceFunctions from '../../..//hooks/useServiceFunctions';
import { DEFAULT_DEBOUNCE_TIME, DEFAULT_PAGE_SIZE } from '../../../consts';
import { useDebouncedValue } from '@mantine/hooks';
import { GetFunctionsQuery } from '@hud/portal-dtos/webapp-function.dto';
import useTimeFrame from '../../../hooks/useTimeFrame';
import TimeFrameMenu from '../../../components/TimeFrameMenu/TimeFrameMenu';
import useEnvironment from '../../../hooks/useEnvironment';
import EnvironmentMenu from '../../../components/EnvironmentMenu/EnvironmentMenu';
import FunctionsTable from '../../../components/FunctionsTable/FunctionsTable';
import ActionsContainer from '../../../components/ActionsContainer/ActionsContainer';
import ThemeButton from '../../../components/ThemeButton/ThemeButton';
import useAnalytics from '../../../hooks/useAnalytics';
import { BreadcrumbItem } from '@hud/web-components/Breadcrumbs/Breadcrumbs';
import { useFunctionFilters } from '../../../hooks/useFunctionFilter';
import { useQueryParamState } from '../../../hooks/useQueryParamState';
import useSharedTableState, {
  CommonTableQueryParams,
} from '../../../hooks/useSharedTableState';
import { ThresholdFilter } from '@hud/portal-dtos/webapp-common.dto';
import {
  CommonThresholdQueryParams,
  getThresholdFilter,
  ThresholdFilterKey,
} from '../../../utils';
import { Events } from '../../../analytics';

const BASE_BREADCRUMBS = [{ title: 'Services', to: '/explore/services', back: 1 }];

type ServicePageQueryParams = CommonTableQueryParams &
  CommonThresholdQueryParams & {
    showUnnamed: boolean;
    files: string[];
  };

export default function ServicePage() {
  const { id } = useParams<{ id: string }>();
  const analytics = useAnalytics();
  const [initialLoad, setInitialLoad] = useState<boolean>(false);

  const { timeFrame } = useTimeFrame();
  const { environment } = useEnvironment();

  const functionFilters = useFunctionFilters();
  const [totalPages, setTotalPages] = useState<number>(0);

  const [pageState, setPageState, resetPageState] =
    useQueryParamState<ServicePageQueryParams>({
      search: '',
      page: 1,
      orderBy: 'normalizedRuns.value',
      desc: true,

      showUnnamed: false,
      files: [],
      durationAvg: undefined,
      durationAvg_op: undefined,
      normalizedRuns: undefined,
      normalizedRuns_op: undefined,
      errorRate: undefined,
      errorRate_op: undefined,
    });

  const tableState = useSharedTableState({
    pageState,
    setPageState,
    resetPageState,
  });

  const clearAllFilters = () => {
    setPageState({
      showUnnamed: false,
      files: [],
      durationAvg: undefined,
      durationAvg_op: undefined,
      normalizedRuns: undefined,
      normalizedRuns_op: undefined,
      errorRate: undefined,
      errorRate_op: undefined,
    });
  };

  const handleShowUnnamedChange = (value: boolean) => {
    setPageState({ showUnnamed: value, page: 1 });
  };

  const handleFilteredFilePathsChange = (value: string[]) => {
    setPageState({ files: value, page: 1 });
  };

  const handleThresholdFilterChange = (
    key: ThresholdFilterKey,
    value: ThresholdFilter | undefined,
    eventName: keyof Events,
  ) => {
    setPageState({
      [key]: value?.value,
      [`${key}_op`]: value?.operator,
      page: 1,
    });

    analytics.capture(eventName, {
      service_id: Number.parseInt(id as string),
      environment: environment?.name,
      toggle_value: !!value,
      filter_value: value?.value ?? 0,
      filter_operator: value?.operator ?? 'gte',
    });
  };

  const handleFilteredDurationAvgChange = (value: ThresholdFilter | undefined) => {
    handleThresholdFilterChange(
      'durationAvg',
      value,
      'service_functions_duration_avg_filter_toggled',
    );
  };

  const handleFilteredNormalizedRunsChange = (value: ThresholdFilter | undefined) => {
    handleThresholdFilterChange(
      'normalizedRuns',
      value,
      'service_functions_normalized_runs_filter_toggled',
    );
  };

  const handleFilteredErrorRateChange = (value: ThresholdFilter | undefined) => {
    handleThresholdFilterChange(
      'errorRate',
      value,
      'service_functions_error_rate_filter_toggled',
    );
  };

  const serviceQuery = useService(parseInt(id as string), {
    environmentName: environment?.name,
    timeFrame: timeFrame,
  });
  const functionsQueryParams = useMemo<GetFunctionsQuery>(
    () => ({
      limit: DEFAULT_PAGE_SIZE,
      offset: (tableState.page || 1) * DEFAULT_PAGE_SIZE - DEFAULT_PAGE_SIZE,
      searchTerms: tableState.search.split(' ').filter(Boolean),
      orderBy: tableState.orderBy as GetFunctionsQuery['orderBy'],
      includeGhost: true,
      includeUnnamed: pageState.showUnnamed,
      timeFrame,
      filePaths: pageState.files,
      environmentName: environment?.name,
      durationAvg: getThresholdFilter(pageState.durationAvg, pageState.durationAvg_op),
      normalizedRuns: getThresholdFilter(
        pageState.normalizedRuns,
        pageState.normalizedRuns_op,
      ),
      errorRate: getThresholdFilter(pageState.errorRate, pageState.errorRate_op),
    }),
    [
      tableState.page,
      tableState.search,
      tableState.orderBy,
      pageState.showUnnamed,
      pageState.files,
      timeFrame,
      environment?.name,
      pageState.durationAvg,
      pageState.durationAvg_op,
      pageState.normalizedRuns,
      pageState.normalizedRuns_op,
      pageState.errorRate,
      pageState.errorRate_op,
    ],
  );
  const [debouncedFunctionsQueryParams] = useDebouncedValue(
    functionsQueryParams,
    DEFAULT_DEBOUNCE_TIME,
  );
  const functionsQuery = useServiceFunctions(
    parseInt(id as string),
    debouncedFunctionsQueryParams,
  );

  useEffect(() => {
    if (functionsQuery.isSuccess) {
      setTotalPages(Math.ceil(functionsQuery.data.countFiltered / DEFAULT_PAGE_SIZE));
    }
  }, [functionsQuery.isSuccess, functionsQuery.data]);

  useEffect(() => {
    if (serviceQuery.isSuccess && !initialLoad) {
      analytics.capture('service_viewed', {
        service_id: Number.parseInt(id as string),
        service_name: serviceQuery.data.service.name,
        environment: environment?.name,
      });
      setInitialLoad(true);
    }
  }, [analytics, environment?.name, id, initialLoad, serviceQuery]);

  useEffect(() => {
    if (serviceQuery.isSuccess) {
      document.title = `${serviceQuery.data?.service.name} • Hud`;
    } else {
      document.title = 'Hud';
    }
  }, [serviceQuery.data?.service.name, serviceQuery.isSuccess]);

  useEffect(() => {
    if (serviceQuery.isSuccess && functionsQuery.isSuccess) {
      analytics.capture('service_functions_paginated', {
        service_id: Number.parseInt(id as string),
        service_name: serviceQuery.data.service.name,
        page: tableState.page,
        column_name: tableState.orderBy?.key ?? '',
        column_order: tableState.orderBy?.sortOrder ?? 'asc',
        search: tableState.search,
        environment: environment?.name,
      });
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState.page]);

  useEffect(() => {
    if (serviceQuery.isSuccess && functionsQuery.isSuccess) {
      analytics.capture('service_functions_searched', {
        service_id: Number.parseInt(id as string),
        service_name: serviceQuery.data.service.name,
        search: tableState.search,
        environment: environment?.name,
      });
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState.search]);

  useEffect(() => {
    if (serviceQuery.isSuccess && functionsQuery.isSuccess) {
      analytics.capture('service_functions_sorted', {
        service_id: Number.parseInt(id as string),
        service_name: serviceQuery.data.service.name,
        column_name: tableState.orderBy?.key ?? '',
        column_order: tableState.orderBy?.sortOrder ?? 'asc',
        environment: environment?.name,
      });
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState.orderBy.key, tableState.orderBy.sortOrder]);

  useEffect(() => {
    if (serviceQuery.isSuccess) {
      analytics.capture('service_functions_hide_unnamed_functions_toggled', {
        service_id: Number.parseInt(id as string),
        service_name: serviceQuery.data.service.name,
        toggle_value: pageState.showUnnamed,
        environment: environment?.name,
      });
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageState.showUnnamed]);

  const referralBreadcrumbs = useMemo(() => {
    return [
      {
        title: 'Service functions',
        to: `/explore/services/${id}?${functionFilters}`,
        nowrap: true,
      },
    ] as BreadcrumbItem[];
  }, [id, functionFilters, serviceQuery]);

  if (serviceQuery.isError) {
    return (
      <Page
        header={BASE_BREADCRUMBS}
        actions={
          <ActionsContainer>
            <EnvironmentMenu />
            <TimeFrameMenu />
            <ThemeButton />
          </ActionsContainer>
        }
      >
        {serviceQuery.error.response?.status === 404
          ? serviceQuery.error.response?.data.message
          : 'Something went wrong'}
      </Page>
    );
  }

  return (
    <Page
      header={
        serviceQuery.isLoading
          ? 'loading'
          : [
              ...BASE_BREADCRUMBS,
              ...(serviceQuery.isSuccess
                ? [{ title: serviceQuery.data.service.name }]
                : []),
            ]
      }
      actions={
        <ActionsContainer>
          <EnvironmentMenu />
          <TimeFrameMenu />
          <ThemeButton />
        </ActionsContainer>
      }
    >
      <ServiceOverview
        service={serviceQuery.data?.service}
        loading={serviceQuery.isLoading}
      />
      <FunctionsTable
        timeFrame={timeFrame}
        aggregatedService={Number.parseInt(id as string)}
        functions={functionsQuery.data?.functions ?? []}
        loading={functionsQuery.isLoading}
        total={functionsQuery.data?.total}
        filtered={functionsQuery.data?.countFiltered}
        searchValue={tableState.search}
        onSearchChange={tableState.handleSearchChange}
        sortingState={tableState.sortingState}
        onSortingChange={tableState.handleSortingChange}
        pages={totalPages}
        currentPage={totalPages ? tableState.page : 0}
        onPageChange={tableState.handlePaginationChange}
        showUnnamed={pageState.showUnnamed}
        onShowUnnamedChange={handleShowUnnamedChange}
        filteredFilePaths={pageState.files}
        onFilteredFilePathsChange={handleFilteredFilePathsChange}
        referralBreadcrumbs={referralBreadcrumbs}
        filteredDurationAvg={getThresholdFilter(
          pageState.durationAvg,
          pageState.durationAvg_op,
        )}
        onFilteredDurationAvgChange={handleFilteredDurationAvgChange}
        filteredNormalizedRuns={getThresholdFilter(
          pageState.normalizedRuns,
          pageState.normalizedRuns_op,
        )}
        onFilteredNormalizedRunsChange={handleFilteredNormalizedRunsChange}
        filteredErrorRate={getThresholdFilter(
          pageState.errorRate,
          pageState.errorRate_op,
        )}
        onFilteredErrorRateChange={handleFilteredErrorRateChange}
        onClearAllFilters={clearAllFilters}
      />
    </Page>
  );
}
